import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[20]

export const KWP_20_INSTALACION_FOTOVOLTAICA: ProgrammaticLandingPageConfig = {
  slug: 'instalacion-fotovoltaica-20-kwp-precio',
  lang: 'es',
  topicId: 'kwp-kosten-20',
  title: 'Instalación fotovoltaica de 20 kWp: precio y valores orientativos',
  metaDescription:
    '¿Cuánto cuesta una instalación fotovoltaica de 20 kWp? Superficie de tejado, número de ' +
    'paneles, desglose de costes y comparación gratuita para viviendas bifamiliares con consumo alto.',
  keywords: [
    'instalación fotovoltaica 20 kWp precio',
    'placas solares 20 kWp coste',
    'cuánto cuesta una instalación de 20 kWp',
    '20 kWp instalación solar bifamiliar',
  ],
  eyebrow: 'Tamaño de instalación: 20 kWp',
  h1: 'Instalación fotovoltaica de 20 kWp: precio y valores orientativos',
  intro:
    'Una instalación de 20 kWp es adecuada para viviendas bifamiliares o viviendas con un consumo ' +
    'total muy elevado. Aquí encontrará valores orientativos sobre superficie de tejado y número ' +
    'de paneles, un desglose de costes y justo debajo una calculadora con el precio medio para ' +
    '20 kWp ya calculado a partir de presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp', kwp: 20 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una instalación de 20 kWp?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 20 kWp suele encajar con una vivienda bifamiliar con dos hogares ' +
        'independientes, o una vivienda unifamiliar con un consumo total muy elevado (bomba de ' +
        'calor, coche eléctrico, quizá piscina), consumo anual total a menudo entre 12.000 y ' +
        '16.000 kWh.',
    },
    { type: 'heading', text: 'Tejado y paneles: lo que necesita una instalación de 20 kWp' },
    {
      type: 'paragraph',
      text:
        `Valor orientativo: unos ${FACTS.roofAreaM2} m² de tejado y aproximadamente ` +
        `${FACTS.moduleCount} paneles con módulos habituales de ~440 Wp. La producción anual, con ` +
        `orientación sur y sin sombras, ronda los ${FACTS.annualYieldKwh.toLocaleString('es-ES')} kWh. ` +
        'A este tamaño, a menudo ya no basta una única vertiente de tejado - un cálculo exacto con ' +
        'varias orientaciones lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Componentes del coste de una instalación fotovoltaica' },
    {
      type: 'list',
      items: [
        'Paneles: el mayor coste individual, normalmente en torno a un tercio del total',
        'Inversor: con 20 kWp suele haber varias cadenas y, a veces, varios inversores',
        'Montaje/mano de obra: fijación en el tejado, cableado, más trabajo si hay varias vertientes',
        'Andamiaje: más extenso cuanto mayor es la superficie de tejado',
        'Instalación eléctrica: adaptación del cuadro eléctrico, posibles contadores separados con dos viviendas',
        'Tramitación: registro ante la distribuidora y organismos correspondientes',
      ],
    },
    {
      type: 'paragraph',
      text:
        'En una vivienda bifamiliar con dos hogares independientes, la empresa instaladora aclara ' +
        'además en el presupuesto cómo se reparte técnica y contablemente la generación y el ' +
        'consumo entre ambas viviendas.',
    },
    { type: 'heading', text: 'Amortización y autoconsumo' },
    {
      type: 'paragraph',
      text:
        '20 kWp sigue estando por debajo del límite de 30 kWp hasta el cual, en Alemania, los ' +
        'ingresos de instalaciones fotovoltaicas están completamente exentos de impuesto sobre la ' +
        'renta y se aplica un 0 % de IVA en la compra e instalación. Un cálculo exacto de ' +
        'autoconsumo y amortización para su tejado lo ofrece la calculadora de dimensionamiento.',
    },
  ],
  faq: [
    {
      q: '¿Cambia algo fiscal o legalmente con una instalación de 20 kWp?',
      a: 'No - 20 kWp sigue estando por debajo del límite de 30 kWp para la exención total del impuesto sobre la renta y el 0 % de IVA en compra e instalación en Alemania.',
    },
    {
      q: '¿Es adecuada una instalación de 20 kWp para una vivienda bifamiliar?',
      a: 'Sí, es uno de los usos típicos de este tamaño. La empresa instaladora aclara en el presupuesto cómo se reparte la generación y el consumo entre las dos viviendas.',
    },
    {
      q: '¿Basta una única vertiente de tejado para 20 kWp?',
      a: `Valor orientativo: se necesitan unos ${FACTS.roofAreaM2} m² - en tejados más pequeños o con formas complicadas, las empresas instaladoras suelen repartir los paneles entre varias vertientes con distinta orientación.`,
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Instalación fotovoltaica de 15 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-15-kwp-precio' },
    { label: 'Instalación fotovoltaica de 25 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-25-kwp-precio' },
    { label: 'Instalación fotovoltaica de 12 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-12-kwp-precio' },
    { label: 'Batería solar de 20 kWh: precio y valores orientativos', href: '/bateria-solar-20-kwh-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
